import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';
import './ClientRoleManagement.css';

function ClientRoleManagement() {
  const { user } = useAuth();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [roleMembers, setRoleMembers] = useState([]);
  const [showMembersModal, setShowMembersModal] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isActive: true
  });

  useEffect(() => {
    fetchRoles();
  }, [user]);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      if (!user?.clientId) {
        setError('No client information available');
        return;
      }

      const response = await api.get(`/admin/roles/client/${user.clientId}`);
      if (response.data.success) {
        setRoles(response.data.roles);
      }

      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        clientId: user.clientId
      };

      const response = editingRole
        ? await api.put(`/admin/roles/${editingRole.GroupID}`, payload)
        : await api.post('/admin/roles', payload);

      if (response.data.success) {
        setSuccess(editingRole ? 'Role updated successfully' : 'Role created successfully');
        resetForm();
        fetchRoles();
      } else {
        setError(response.data.message || 'Failed to save role');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save role');
    }
  };

  const handleEdit = (roleData) => {
    setEditingRole(roleData);
    setFormData({
      name: roleData.GroupName,
      description: roleData.GroupDescription || '',
      isActive: roleData.IsActive
    });
    setShowCreateForm(true);
  };

  const handleDelete = async (groupId) => {
    if (window.confirm('Are you sure you want to delete this role? All user associations will be removed.')) {
      try {
        const response = await api.delete(`/admin/roles/${groupId}`);
        if (response.data.success) {
          setSuccess('Role deleted successfully');
          fetchRoles();
        } else {
          setError(response.data.message || 'Failed to delete role');
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to delete role');
      }
    }
  };

  const handleViewMembers = async (roleData) => {
    try {
      const response = await api.get(`/admin/roles/${roleData.GroupID}/members`);
      if (response.data.success) {
        setSelectedRole(roleData);
        setRoleMembers(response.data.members);
        setShowMembersModal(true);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load role members');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      isActive: true
    });
    setEditingRole(null);
    setShowCreateForm(false);
  };

  if (loading) {
    return <div className="role-management-container"><div className="loading">Loading role management...</div></div>;
  }

  return (
    <div className="role-management-container">
      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="management-header">
        <h2>Role Management</h2>
        <button 
          className="btn btn-primary"
          onClick={() => setShowCreateForm(!showCreateForm)}
        >
          {showCreateForm ? 'Cancel' : '+ Create New Role'}
        </button>
      </div>

      {showCreateForm && (
        <div className="form-section">
          <h3>{editingRole ? 'Edit Role' : 'Create New Role'}</h3>
          <form onSubmit={handleSubmit} className="role-form">
            <div className="form-group">
              <label>Role Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="e.g., Administrator, Manager, Viewer"
                required
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Describe the purpose and permissions of this role"
                rows="3"
              />
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleInputChange}
                />
                Active
              </label>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-success">
                {editingRole ? 'Update Role' : 'Create Role'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="roles-section">
        <h3>Roles ({roles.length})</h3>
        {roles.length === 0 ? (
          <p className="no-data">No roles found. Create your first role to get started.</p>
        ) : (
          <div className="roles-grid">
            {roles.map(role => (
              <div key={role.GroupID} className="role-card">
                <div className="role-header">
                  <h4>{role.GroupName}</h4>
                  <div className="role-actions">
                    <button
                      className="btn-icon edit"
                      onClick={() => handleEdit(role)}
                      title="Edit role"
                    >
                      ✎
                    </button>
                    <button
                      className="btn-icon delete"
                      onClick={() => handleDelete(role.GroupID)}
                      title="Delete role"
                    >
                      🗑
                    </button>
                  </div>
                </div>

                <p className="role-description">{role.GroupDescription || 'No description'}</p>

                <div className="role-info">
                  <span className={`status ${role.IsActive ? 'active' : 'inactive'}`}>
                    {role.IsActive ? 'Active' : 'Inactive'}
                  </span>
                  <span className="created-date">
                    Created: {new Date(role.CreatedDate).toLocaleDateString()}
                  </span>
                </div>

                <button
                  className="btn btn-info view-members"
                  onClick={() => handleViewMembers(role)}
                >
                  View Members
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {showMembersModal && selectedRole && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h4>Members of {selectedRole.GroupName}</h4>
              <button
                className="modal-close"
                onClick={() => setShowMembersModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {roleMembers.length === 0 ? (
                <p className="no-data">No members assigned to this role yet.</p>
              ) : (
                <div className="members-list">
                  <table>
                    <thead>
                      <tr>
                        <th>Username</th>
                        <th>Full Name</th>
                        <th>Email</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {roleMembers.map(member => (
                        <tr key={member.UserID}>
                          <td>{member.UserName}</td>
                          <td>{member.FullName}</td>
                          <td>{member.Email}</td>
                          <td>
                            <span className={`status ${member.IsActive ? 'active' : 'inactive'}`}>
                              {member.IsActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClientRoleManagement;
